<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import {
  detectAuthMode,
  getBridge,
  getStartParamMode,
  getStartParamNumber,
  isMockMode,
} from './max-bridge';
import {
  fetchMyPending,
  fetchUploadInfo,
  fetchWhoAmI,
  generateUuid,
  meByCookie,
  replaceScan,
  uploadScan,
  type UploadInfo,
  type WhoAmI,
} from './api';
import { warpWithCorners, type DetectedCorners } from './scanner';
import { applyFilter, type FilterMode } from './filters';
import ScannerCamera from './components/ScannerCamera.vue';
import ThumbStrip from './components/ThumbStrip.vue';
import ManualCornerEditor from './components/ManualCornerEditor.vue';
import AdminUsers from './components/AdminUsers.vue';
import AdminContracts from './components/AdminContracts.vue';
import AdminAudit from './components/AdminAudit.vue';
import AdminOcrMetrics from './components/AdminOcrMetrics.vue';
import LoginScreen from './components/LoginScreen.vue';

type Step = 'intro' | 'capture' | 'manual' | 'review';
type Tab = 'scanner' | 'contracts' | 'users' | 'audit' | 'ocr';

// TASK-019: текущий пользователь — определяет какие табы показывать
const me = ref<WhoAmI | null>(null);
const currentTab = ref<Tab>('scanner');

// 2026-05-25: менеджер отдела продаж (роль «менеджер») получает доступ
// к табам Договоры/Сканер вручную/Сброс сессий, но НЕ видит Users/Audit/OCR.
// `is_admin` — только роль ADMIN; `isManagerOrAdmin` — оба.
const isManagerOrAdmin = computed(
  () => me.value?.is_admin === true || me.value?.role === 'менеджер',
);

// TASK-006 W3: auth mode + show LoginScreen если ни cookie ни initData нет
const authMode = ref<'max_initdata' | 'cookie' | 'magic_link' | 'checking'>('checking');
const showLogin = computed(() => authMode.value === 'magic_link' && !me.value);

async function handleAuthenticated(): Promise<void> {
  // После успешного magic-link login — перезагрузить страницу
  // чтобы App.vue заново прогнал onMounted с cookie
  window.location.reload();
}

const bridge = getBridge();
const step = ref<Step>('intro');
const pages = ref<Array<Blob | File>>([]); // JPEG/PNG/PDF каждой страницы
const pendingCanvas = ref<HTMLCanvasElement | null>(null); // для ManualCornerEditor
// Retake-режим: если != null — следующий capture/манual confirm заменит pages[retakeIndex],
// а не добавит в конец. Сбрасывается после успешного capture или отмены.
const retakeIndex = ref<number | null>(null);
// Фильтр для постобработки скана (TASK Adobe-Scan-like день 7).
// 'original' = без обработки (по умолчанию),
// 'enhance'  = контраст + sharpen,
// 'bw'       = adaptiveThreshold чёрно-белое (идеально для печатных договоров).
const filterMode = ref<FilterMode>('original');
const clientName = ref('');
const number = ref<number | null>(getStartParamNumber());

// Загрузка под ПРОИЗВОЛЬНЫЙ номер (не только подхваченный my-pending).
// Выездной так дополняет любой из своих номеров; менеджер/админ — за коллег.
const showManualNumber = ref(false);
const manualNumberInput = ref('');
const uploadInfo = ref<UploadInfo | null>(null);
const checkingNumber = ref(false);

const isUploading = ref(false);
const uploadError = ref<string | null>(null);
const uploadSuccess = ref(false);

// TASK-017: режим работы. 'new' = первая загрузка (ФИО обязательно).
// 'replace' = замена существующего скана (ФИО не трогаем, причина опц.).
// Активируется через MAX initData start_param «n=425;mode=replace», URL
// ?mode=replace, или кнопкой «🔁 Заменить» на intro.
const mode = ref<'new' | 'replace'>(getStartParamMode());
const replaceReason = ref('');

// P0.4 — submission_id генерируется ОДИН раз и переиспользуется на retry.
// Сервер по idempotency-table вернёт тот же ответ → нет дубля файла.
const submissionId = ref<string>(generateUuid());

const isReadyToSend = computed(() => {
  if (pages.value.length === 0 || number.value === null) return false;
  // В режиме replace ФИО не нужно — оно уже в БД.
  if (mode.value === 'replace') return true;
  return clientName.value.trim().length >= 3;
});

onMounted(async () => {
  // TASK-006 W3: detect auth mode перед всеми API-вызовами
  const mode = detectAuthMode();
  authMode.value = mode;
  console.info('[auth] detected mode:', mode);

  // Сначала пробуем cookie (если был magic-link login раньше)
  if (mode === 'cookie') {
    try {
      const meResp = await meByCookie();
      // Маппинг auth/me → WhoAmI (см. api.ts WhoAmI interface)
      me.value = {
        engineer_id: meResp.max_user_id,
        role: meResp.role as WhoAmI['role'],
        active: true,
        district: null,
        full_name_override: meResp.full_name,
        is_admin: meResp.is_admin,
      };
      console.info('[whoami] via cookie:', me.value);
    } catch (err) {
      console.warn('[whoami] cookie failed → fallback to initData', err);
      authMode.value = 'magic_link';
    }
  }

  // Если cookie не дала результат — пробуем MAX initData (как раньше)
  if (!me.value && mode !== 'magic_link') {
    try {
      const { getInitData } = await import('./max-bridge');
      const initData = getInitData();
      console.info('[whoami] initData source check:', {
        hashPresent: !!window.location.hash,
        hashLen: window.location.hash.length,
        bridgeInitDataLen: bridge.initData?.length || 0,
        effectiveInitDataLen: initData.length,
      });
      me.value = await fetchWhoAmI();
      console.info('[whoami] via initData:', me.value);
    } catch (err) {
      console.warn('[whoami] initData failed', err);
      // Открыто из MAX, но HMAC сломан → показываем LoginScreen с magic-link
      authMode.value = 'magic_link';
    }
  }

  // Авто-подхват номера через API (ADR-016 revision 2).
  // MAX блокирует передачу параметров через OpenAppButton (и payload, и URL),
  // поэтому если start_param/URL пустые — спрашиваем Numerator по initData HMAC:
  // «есть ли у меня незакрытый номер?».
  if (number.value === null) {
    try {
      const pending = await fetchMyPending();
      if (pending.number !== null) {
        number.value = pending.number;
        // Если ФИО уже задано в БД — заполним поле автоматически (выездной
        // не будет переписывать). Mini App в режиме 'new' покажет «прислать скан».
        if (pending.client_name) {
          clientName.value = pending.client_name;
        }
        console.info('[my-pending] auto-attached', pending);
      } else {
        console.warn('[my-pending] нет незакрытых номеров — пользователь возьмёт через бота');
      }
    } catch (err) {
      console.warn('[my-pending] failed', err);
    }
  }
  // TASK-006 / PWA Web Share Target:
  // SW обработал POST /share-receive/ от системного share-sheet,
  // положил Blob в cache 'shared-incoming/shared-latest', редиректнул сюда
  // с ?source=share. Достаём, добавляем как страницу.
  const params = new URLSearchParams(window.location.search);
  if (params.get('source') === 'share') {
    try {
      const cache = await caches.open('shared-incoming');
      const res = await cache.match('/shared-latest');
      if (res) {
        const filename = res.headers.get('X-Filename') || 'shared';
        const ctype = res.headers.get('X-Type') || 'application/octet-stream';
        const blob = await res.blob();
        // Используем File чтобы был type — pdf-lib его прочитает
        const file = new File([blob], filename, { type: ctype });
        pages.value.push(file);
        bridge.HapticFeedback?.notificationOccurred('success');
        step.value = 'review';
        console.info('[share-target] attached', filename, ctype, blob.size);
        // Cleanup cache
        await cache.delete('/shared-latest');
        // Очистить query-param чтобы не повторно при reload
        window.history.replaceState({}, '', window.location.pathname);
      }
    } catch (err) {
      console.warn('[share-target] read failed', err);
    }
  }

  // TASK-006: префетч jscanify сразу после mount.
  // Грузится в фоне — UI отзывчив. Если пользователь нажмёт «Сканировать»
  // ровно сразу — увидит loader, но обычно 12 MB качается за время
  // ввода ФИО.
  const { prefetchScanner } = await import('./scanner');
  prefetchScanner();

  // Лог UA → выяснить версию Chromium WebView MAX (для письма в partner_support
  // про onPermissionRequest и getUserMedia)
  try {
    const params2 = new URLSearchParams({
      ua: navigator.userAgent,
      platform: (navigator as any).platform || '',
      hasMediaDevices: String(!!navigator.mediaDevices),
      hasGetUserMedia: String(!!navigator.mediaDevices?.getUserMedia),
    });
    fetch(`/api/v1/debug/client-info?${params2}`, { method: 'POST' }).catch(() => {});
  } catch {
    // ignore
  }
});

function startCapture() {
  step.value = 'capture';
}

async function applyNewPage(blob: Blob | File): Promise<void> {
  // Применяем выбранный фильтр (если это не PDF). PDF копируется как есть.
  let processed: Blob | File = blob;
  const isPdf = blob.type === 'application/pdf';
  if (!isPdf && filterMode.value !== 'original') {
    try {
      processed = await applyFilter(blob, filterMode.value);
    } catch (err) {
      console.warn('filter failed, using original', err);
    }
  }
  // Если в retake-режиме — заменяем существующую страницу с сохранением порядка.
  if (retakeIndex.value !== null) {
    const idx = retakeIndex.value;
    if (idx >= 0 && idx < pages.value.length) {
      pages.value[idx] = processed;
    } else {
      pages.value.push(processed);
    }
    retakeIndex.value = null;
  } else {
    pages.value.push(processed);
  }
}

async function onPageCaptured(jpeg: Blob) {
  await applyNewPage(jpeg);
  bridge.HapticFeedback?.notificationOccurred('success');
  step.value = 'review';
}

// «📎 Прикрепить готовый файл» — для случаев когда выездной уже отсканировал
// в Microsoft Lens / iOS Notes / Samsung Notes и хочет приложить готовый PDF.
// Также принимаем обычные изображения (если выбрал из галереи фото договора).
const attachInputEl = ref<HTMLInputElement | null>(null);

function openAttach() {
  attachInputEl.value?.click();
}

async function onAttachPicked(e: Event) {
  const input = e.target as HTMLInputElement;
  const files = input.files;
  if (!files || files.length === 0) return;
  bridge.HapticFeedback?.impactOccurred('medium');
  for (const file of Array.from(files)) {
    if (file.type === 'application/pdf') {
      // PDF от стороннего сканера — прикрепляем как одну «страницу» (без фильтра).
      await applyNewPage(file);
    } else if (file.type.startsWith('image/')) {
      // Картинка — применяем фильтр (если выбран) и кладём как страницу.
      await applyNewPage(file);
    }
  }
  input.value = '';  // reset чтобы можно было снова приложить тот же файл
  step.value = 'review';
}

function onManualNeeded(canvas: HTMLCanvasElement) {
  pendingCanvas.value = canvas;
  bridge.HapticFeedback?.impactOccurred('medium');
  step.value = 'manual';
}

async function onManualConfirmed(corners: DetectedCorners) {
  if (!pendingCanvas.value) return;
  try {
    const jpeg = await warpWithCorners(pendingCanvas.value, corners);
    await applyNewPage(jpeg);
    bridge.HapticFeedback?.notificationOccurred('success');
  } catch (err) {
    console.error('manual_warp_failed', err);
    uploadError.value = 'Не удалось обработать фото. Попробуйте ещё раз.';
  } finally {
    pendingCanvas.value = null;
    step.value = 'review';
  }
}

function onManualCancelled() {
  pendingCanvas.value = null;
  step.value = pages.value.length > 0 ? 'review' : 'intro';
}

function addAnother() {
  retakeIndex.value = null;  // обычный режим: новая страница в конец
  step.value = 'capture';
}

function removePage(idx: number) {
  pages.value.splice(idx, 1);
  if (pages.value.length === 0) step.value = 'intro';
}

function onRetake(idx: number) {
  // Запоминаем индекс — следующий capture заменит pages[idx] вместо append
  retakeIndex.value = idx;
  step.value = 'capture';
}

function onReordered(newPages: Array<Blob | File>) {
  pages.value = newPages;
}

function onCaptureCancelled() {
  // Если отменили retake — старая страница остаётся
  retakeIndex.value = null;
  step.value = pages.value.length > 0 ? 'review' : 'intro';
}

async function submit() {
  if (!isReadyToSend.value || number.value === null) return;
  uploadError.value = null;
  isUploading.value = true;
  try {
    if (mode.value === 'replace') {
      await replaceScan({
        number: number.value,
        reason: replaceReason.value,
        pages: pages.value,
        submissionId: submissionId.value,
      });
    } else {
      await uploadScan({
        number: number.value,
        clientName: clientName.value.trim(),
        pages: pages.value,
        submissionId: submissionId.value,
      });
    }
    uploadSuccess.value = true;
    bridge.HapticFeedback?.notificationOccurred('success');
    setTimeout(() => bridge.close(), 1500);
  } catch (err) {
    bridge.HapticFeedback?.notificationOccurred('error');
    uploadError.value = err instanceof Error ? err.message : String(err);
  } finally {
    isUploading.value = false;
  }
}

function toggleMode() {
  mode.value = mode.value === 'new' ? 'replace' : 'new';
  uploadError.value = null;
}

// Проверить введённый вручную номер через upload-info и переключиться на него.
async function applyManualNumber() {
  const n = parseInt(manualNumberInput.value.trim(), 10);
  if (!Number.isInteger(n) || n < 1 || n > 99999) {
    uploadInfo.value = null;
    return;
  }
  checkingNumber.value = true;
  uploadError.value = null;
  try {
    const info = await fetchUploadInfo(n);
    uploadInfo.value = info;
    if (!info.exists) {
      uploadError.value = `Договор № ${n} не найден.`;
      return;
    }
    if (!info.can_upload) {
      uploadError.value =
        `№ ${n} закреплён за другим (${info.engineer_name ?? '—'}). ` +
        `Загрузить чужой скан может только менеджер или админ.`;
      return;
    }
    // Принять номер. Если уже есть ФИО — подставим. Если уже есть скан —
    // загрузка станет заменой (предупреждаем в UI).
    number.value = n;
    if (info.client_name) clientName.value = info.client_name;
    showManualNumber.value = false;
  } catch (err) {
    uploadError.value = err instanceof Error ? err.message : String(err);
  } finally {
    checkingNumber.value = false;
  }
}

// Предупреждение про замену: выбранный номер уже имеет скан.
const willReplaceExisting = computed(
  () => uploadInfo.value?.number === number.value && uploadInfo.value?.has_scan === true,
);
</script>

<template>
  <!-- TASK-006 W3: показ LoginScreen для пользователей открывших страницу в обычном
       браузере (вне MAX-клиента) — magic-link авторизация заменяет HMAC initData -->
  <LoginScreen v-if="showLogin" @authenticated="handleAuthenticated" />

  <main v-else class="flex flex-col min-h-screen p-4 pb-16">
    <!-- Scanner tab (default) -->
    <template v-if="currentTab === 'scanner'">
    <header class="mb-4">
      <h1 class="text-xl font-semibold">
        Сканер договора
        <span v-if="mode === 'replace'" class="text-orange-500 text-sm font-normal">
          · режим замены
        </span>
      </h1>
      <p v-if="number !== null" class="text-hint text-sm mt-1">
        Договор № {{ number }}
        <span v-if="mode === 'replace'"> (заменяем скан)</span>
      </p>
      <p v-else class="text-orange-500 text-sm mt-1">
        Нет незакрытого договора. Сначала возьмите номер в боте
        («договор»), потом откройте сканер.
      </p>
    </header>

    <div v-if="isMockMode()" class="bg-yellow-100 text-yellow-900 text-xs p-2 rounded mb-4">
      Тестовый режим: мост MAX не найден. Откройте через MAX для боевой работы.
    </div>

    <!-- intro -->
    <section v-if="step === 'intro' && !uploadSuccess" class="flex flex-col gap-4 flex-1">
      <p v-if="mode === 'new'" class="text-base">
        Сфотографируйте все страницы подписанного договора. После съёмки введите ФИО клиента и
        отправьте.
      </p>
      <p v-else class="text-base">
        <b>🔁 Замена скана.</b> Сфотографируйте новую версию договора № {{ number }}.
        Номер, дата и ФИО клиента остаются прежними — заменится только сам файл.
      </p>

      <!-- TASK-017: переключатель режима -->
      <div v-if="number !== null" class="text-center">
        <button
          type="button"
          class="text-sm text-orange-600 underline"
          @click="toggleMode"
        >
          {{ mode === 'new' ? '🔁 Заменить существующий скан' : '← Загрузить как новый' }}
        </button>
      </div>

      <!-- TASK-030 C2: заметный выбор номера для менеджера/админа.
           Админ/менеджер работают с разными договорами в любом порядке — поле
           выбора номера всегда на виду, а не спрятано за мелкой ссылкой.
           Для выездного — прежняя компактная ссылка (дополняет свои номера). -->
      <div
        v-if="isManagerOrAdmin"
        class="bg-blue-50 border border-blue-200 rounded-lg p-3 flex flex-col gap-2"
      >
        <label class="block text-sm font-medium text-blue-900">
          Загрузить скан к договору №
        </label>
        <div class="flex gap-2 items-center">
          <input
            v-model="manualNumberInput"
            type="number"
            inputmode="numeric"
            :placeholder="number !== null ? String(number) : 'например 668'"
            class="input flex-1 text-center"
            @keyup.enter="applyManualNumber"
          />
          <button
            type="button"
            class="btn-secondary text-sm whitespace-nowrap"
            :disabled="checkingNumber"
            @click="applyManualNumber"
          >
            {{ checkingNumber ? '...' : 'Выбрать' }}
          </button>
        </div>
        <p v-if="number !== null" class="text-xs text-blue-700">
          Сейчас выбран № {{ number }}{{ clientName ? ` — ${clientName}` : '' }}.
          Введите другой номер, чтобы переключиться.
        </p>
      </div>

      <!-- Загрузка под другой (произвольный) номер — компактно для выездного -->
      <div v-else class="text-center">
        <button
          v-if="!showManualNumber"
          type="button"
          class="text-sm text-blue-600 underline"
          @click="showManualNumber = true"
        >
          № Загрузить под другой номер
        </button>
        <div v-else class="flex flex-col gap-2 items-center">
          <div class="flex gap-2 items-center justify-center">
            <input
              v-model="manualNumberInput"
              type="number"
              inputmode="numeric"
              placeholder="например 518"
              class="input w-32 text-center"
              @keyup.enter="applyManualNumber"
            />
            <button
              type="button"
              class="btn-secondary text-sm"
              :disabled="checkingNumber"
              @click="applyManualNumber"
            >
              {{ checkingNumber ? '...' : 'Проверить' }}
            </button>
          </div>
          <button
            type="button"
            class="text-xs text-hint underline"
            @click="showManualNumber = false"
          >
            отмена
          </button>
        </div>
      </div>

      <!-- Предупреждение: у выбранного номера уже есть скан → замена -->
      <div
        v-if="willReplaceExisting"
        class="bg-orange-100 text-orange-900 text-sm p-2 rounded"
      >
        ⚠ У договора № {{ number }} уже есть скан. Загрузка ЗАМЕНИТ его на новый.
      </div>

      <div v-if="mode === 'new'">
        <label for="client-name" class="block text-sm font-medium mb-1">ФИО клиента</label>
        <input
          id="client-name"
          v-model="clientName"
          type="text"
          autocomplete="off"
          placeholder="Иванов Иван Иванович"
          class="input"
        />
      </div>

      <div v-else>
        <label for="replace-reason" class="block text-sm font-medium mb-1">
          Причина замены (опционально)
        </label>
        <input
          id="replace-reason"
          v-model="replaceReason"
          type="text"
          autocomplete="off"
          placeholder="Например: заменили станцию"
          class="input"
        />
      </div>

      <div>
        <label class="block text-sm font-medium mb-1">Качество скана</label>
        <div class="filter-toggle">
          <button
            type="button"
            class="filter-opt"
            :class="{ active: filterMode === 'original' }"
            @click="filterMode = 'original'"
          >
            Оригинал
          </button>
          <button
            type="button"
            class="filter-opt"
            :class="{ active: filterMode === 'enhance' }"
            @click="filterMode = 'enhance'"
          >
            🎨 Усилить
          </button>
          <button
            type="button"
            class="filter-opt"
            :class="{ active: filterMode === 'bw' }"
            @click="filterMode = 'bw'"
          >
            ⚫ Ч/Б
          </button>
        </div>
        <p class="text-xs text-hint mt-1">
          «Усилить» = контраст + резкость. «Ч/Б» = идеально для печатных договоров
          (PDF в 3-5× меньше).
        </p>
      </div>

      <button class="btn" @click="startCapture">📷 Сделать первую страницу</button>

      <div class="text-center text-sm text-hint">или</div>

      <button class="btn btn-secondary" @click="openAttach">
        📎 Прикрепить готовый PDF / фото
      </button>
      <input
        ref="attachInputEl"
        type="file"
        accept="application/pdf,image/*"
        multiple
        class="hidden"
        @change="onAttachPicked"
      />

      <div class="text-xs text-hint mt-2 p-3 bg-secondary-bg rounded">
        💡 <b>Совет</b>: для качественного скана используйте<br />
        • <b>iPhone</b>: Заметки → «+» → «Сканировать документы»<br />
        • <b>Android</b>: <b>Microsoft Lens</b> (бесплатно, без рекламы), Заметки Samsung<br />
        Сохраните PDF и нажмите «Прикрепить готовый PDF».
      </div>
    </section>

    <!-- capture (full-screen) -->
    <ScannerCamera
      v-else-if="step === 'capture'"
      @captured="onPageCaptured"
      @manual-needed="onManualNeeded"
      @cancelled="onCaptureCancelled"
    />

    <!-- manual correction (full-screen) -->
    <ManualCornerEditor
      v-else-if="step === 'manual' && pendingCanvas"
      :source-canvas="pendingCanvas"
      @confirmed="onManualConfirmed"
      @cancelled="onManualCancelled"
    />

    <!-- review -->
    <section v-else-if="step === 'review' && !uploadSuccess" class="flex flex-col gap-4 flex-1">
      <ThumbStrip
        :pages="pages"
        @reordered="onReordered"
        @retake="onRetake"
        @remove="removePage"
      />

      <div v-if="mode === 'new'">
        <label for="client-name-review" class="block text-sm font-medium mb-1">ФИО клиента</label>
        <input
          id="client-name-review"
          v-model="clientName"
          type="text"
          autocomplete="off"
          placeholder="Иванов Иван Иванович"
          class="input"
        />
      </div>

      <div v-else>
        <label for="replace-reason-review" class="block text-sm font-medium mb-1">
          Причина замены (опционально)
        </label>
        <input
          id="replace-reason-review"
          v-model="replaceReason"
          type="text"
          autocomplete="off"
          placeholder="Например: заменили станцию"
          class="input"
        />
      </div>

      <div class="flex gap-2">
        <button
          class="btn btn-secondary flex-1"
          :disabled="isUploading"
          @click="addAnother"
        >
          + Ещё страница
        </button>
        <button class="btn flex-1" :disabled="!isReadyToSend || isUploading" @click="submit">
          {{ isUploading ? 'Отправка...' : (mode === 'replace' ? '🔁 Заменить' : '✅ Отправить') }}
        </button>
      </div>

      <p v-if="!isReadyToSend && pages.length > 0 && mode === 'new'" class="text-hint text-xs">
        Заполните ФИО клиента (минимум 3 символа), чтобы отправить.
      </p>

      <p v-if="uploadError" class="text-red-500 text-sm">{{ uploadError }}</p>
    </section>

    <!-- success -->
    <section
      v-else-if="uploadSuccess"
      class="flex flex-col items-center justify-center flex-1 gap-2"
    >
      <div class="text-6xl">{{ mode === 'replace' ? '🔁' : '✅' }}</div>
      <p class="text-lg font-medium">
        Договор № {{ number }} {{ mode === 'replace' ? 'заменён' : 'загружен' }}
      </p>
      <p class="text-hint text-sm">Возвращаемся в чат...</p>
    </section>
    </template>

    <!-- Contracts tab — менеджер тоже может править ФИО / b24 / beneficiary -->
    <AdminContracts
      v-else-if="currentTab === 'contracts' && isManagerOrAdmin"
      :is-admin="me?.is_admin === true"
    />

    <!-- Users tab — только админ (CRUD whitelist) -->
    <AdminUsers v-else-if="currentTab === 'users' && me?.is_admin" />

    <!-- Audit tab — только админ -->
    <AdminAudit v-else-if="currentTab === 'audit' && me?.is_admin" />

    <!-- OCR metrics tab (TASK-023 W3) — только админ -->
    <AdminOcrMetrics v-else-if="currentTab === 'ocr' && me?.is_admin" />

    <!-- Tabbar — для админа и менеджера -->
    <nav
      v-if="isManagerOrAdmin"
      class="fixed bottom-0 left-0 right-0 bg-white border-t flex justify-around z-40"
      style="padding-bottom: env(safe-area-inset-bottom, 0px)"
    >
      <button
        class="flex-1 py-3 text-sm"
        :class="currentTab === 'scanner' ? 'text-blue-600 font-medium' : 'text-gray-500'"
        @click="currentTab = 'scanner'"
      >
        📷 Сканер
      </button>
      <button
        class="flex-1 py-3 text-sm"
        :class="currentTab === 'contracts' ? 'text-blue-600 font-medium' : 'text-gray-500'"
        @click="currentTab = 'contracts'"
      >
        📋 Договоры
      </button>
      <!-- Люди / Аудит / OCR — только админу -->
      <button
        v-if="me?.is_admin"
        class="flex-1 py-3 text-sm"
        :class="currentTab === 'users' ? 'text-blue-600 font-medium' : 'text-gray-500'"
        @click="currentTab = 'users'"
      >
        👥 Люди
      </button>
      <button
        v-if="me?.is_admin"
        class="flex-1 py-3 text-sm"
        :class="currentTab === 'audit' ? 'text-blue-600 font-medium' : 'text-gray-500'"
        @click="currentTab = 'audit'"
      >
        📜 Аудит
      </button>
      <button
        v-if="me?.is_admin"
        class="flex-1 py-3 text-sm"
        :class="currentTab === 'ocr' ? 'text-blue-600 font-medium' : 'text-gray-500'"
        @click="currentTab = 'ocr'"
      >
        🔍 OCR
      </button>
    </nav>
  </main>
</template>

<style scoped>
/* Filter toggle (TASK Adobe-Scan-like день 7) */
.filter-toggle {
  display: flex;
  gap: 6px;
  background: #f1f3f5;
  padding: 4px;
  border-radius: 10px;
}
.filter-opt {
  flex: 1;
  padding: 8px 10px;
  border: none;
  background: transparent;
  border-radius: 7px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #4b5563;
  cursor: pointer;
  transition: all 120ms ease;
}
.filter-opt:active {
  transform: scale(0.96);
}
.filter-opt.active {
  background: white;
  color: #2563eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
</style>
